import { Check, ChevronsUpDown, School as SchoolIcon, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

/**
 * Switches the active School. Updates client state + the X-School header and
 * clears cached school-scoped data so it refetches for the new school.
 */
export function SchoolSwitcher() {
  const { t } = useTranslation('nav');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const memberships = useSession(s => s.memberships);
  const activeSchoolSlug = useSession(s => s.activeSchoolSlug);
  const setActiveSchool = useSession(s => s.setActiveSchool);

  const active = memberships.find(m => m.school.slug === activeSchoolSlug)?.school;

  if (memberships.length === 0) return null;

  function selectSchool(slug: string) {
    if (slug === activeSchoolSlug) return;
    setActiveSchool(slug);
    qc.clear(); // drop all cached server data — it was scoped to the previous school
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="max-w-[12rem] justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <SchoolIcon className="size-4 shrink-0" />
            <span className="truncate">{active?.name ?? t('school.select')}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t('school.switcherLabel')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map(({ school }) => (
          <DropdownMenuItem
            key={school.id}
            onSelect={() => selectSchool(school.slug)}
            className="justify-between"
          >
            <span className="truncate">{school.name}</span>
            <Check
              className={cn(
                'size-4',
                school.slug === activeSchoolSlug ? 'opacity-100' : 'opacity-0'
              )}
            />
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/onboarding/create-school')}>
          <Plus />
          {t('school.create')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
